import { setup } from 'xstate';

/**
 * F-017 `occurrenceDecisionMachine` (§XI — data-model §2.1). Owns the
 * reviewer-facing lifecycle of one occurrence's applicability decision. The
 * server is the transition authority: use cases restore a snapshot from the
 * persisted status, send CONFIRM/OVERRIDE, and persist the resulting state.
 * Every transition is audited + emits a Correction Record atomically
 * (I-SI-07) — by the use case, not here (the domain machine is pure).
 *
 * Entry states come from generation: `decided` (confidence = decided) or
 * `confirmPending` (low_confidence | undecidable → include + flag, FR-010).
 * OVERRIDE requires a reason (guard) and is legal from every state including
 * `overridden` itself (re-override allowed, each audited).
 */
export type OccurrenceDecisionEvent =
  | { readonly type: 'CONFIRM' }
  | { readonly type: 'OVERRIDE'; readonly applicable: boolean; readonly reason: string };

export const occurrenceDecisionMachine = setup({
  types: {} as {
    events: OccurrenceDecisionEvent;
  },
  guards: {
    hasReason: ({ event }) => event.type === 'OVERRIDE' && event.reason.trim().length > 0,
  },
}).createMachine({
  id: 'occurrenceDecision',
  initial: 'decided',
  states: {
    decided: {
      on: {
        OVERRIDE: { target: 'overridden', guard: 'hasReason' },
      },
    },
    confirmPending: {
      on: {
        CONFIRM: { target: 'confirmed' },
        OVERRIDE: { target: 'overridden', guard: 'hasReason' },
      },
    },
    confirmed: {
      on: {
        OVERRIDE: { target: 'overridden', guard: 'hasReason' },
      },
    },
    overridden: {
      on: {
        OVERRIDE: { target: 'overridden', guard: 'hasReason' }, // re-override, each audited
      },
    },
  },
});

/**
 * The wire/persisted enum ↔ XState node-id mapping, DECLARED ONCE (data-model
 * §2.1 naming mapping): node ids are camelCase (`confirmPending`); the wire
 * enum `OccurrenceDecisionStatus` carries the same states as snake_case
 * values (`confirm_pending`). No third spelling exists.
 */
export const OCCURRENCE_DECISION_STATUSES = [
  'decided',
  'confirm_pending',
  'confirmed',
  'overridden',
] as const;

export type OccurrenceDecisionStatus = (typeof OCCURRENCE_DECISION_STATUSES)[number];

export type OccurrenceDecisionState = 'decided' | 'confirmPending' | 'confirmed' | 'overridden';

const STATE_BY_STATUS: Record<OccurrenceDecisionStatus, OccurrenceDecisionState> = {
  decided: 'decided',
  confirm_pending: 'confirmPending',
  confirmed: 'confirmed',
  overridden: 'overridden',
};

const STATUS_BY_STATE: Record<OccurrenceDecisionState, OccurrenceDecisionStatus> = {
  decided: 'decided',
  confirmPending: 'confirm_pending',
  confirmed: 'confirmed',
  overridden: 'overridden',
};

export const isOccurrenceDecisionStatus = (raw: string): raw is OccurrenceDecisionStatus =>
  (OCCURRENCE_DECISION_STATUSES as readonly string[]).includes(raw);

export const parseOccurrenceDecisionStatus = (raw: string): OccurrenceDecisionStatus => {
  if (!isOccurrenceDecisionStatus(raw)) {
    throw new Error(`Invalid OccurrenceDecisionStatus: ${JSON.stringify(raw)}`);
  }
  return raw;
};

export const occurrenceDecisionStateFromStatus = (
  status: OccurrenceDecisionStatus,
): OccurrenceDecisionState => STATE_BY_STATUS[status];

export const occurrenceDecisionStatusFromState = (
  state: OccurrenceDecisionState,
): OccurrenceDecisionStatus => STATUS_BY_STATE[state];
